import { useState } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { useNavigate, useParams } from "react-router-dom";
import { useRoleEditor } from "./useRoleEditor";
import { useDeleteRole } from "./useDeleteRole";
import { PermissionCheckboxGroup } from "./PermissionCheckboxGroup";
import { DeleteRoleDialog } from "./DeleteRoleDialog";
import { IfPermission } from "../../auth/IfPermission";

export function RoleEditor() {
  const navigate = useNavigate();
  const parameters = useParams<{ id: string }>();
  const editor = useRoleEditor(() => {
      void navigate("/roles");
    });
  const { deletion, deleteRole, resetDeletion } = useDeleteRole(() => {
      void navigate("/roles");
    });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const confirmDelete = () => {
    setDeleteDialogOpen(false);
    if (parameters.id !== undefined) {
      deleteRole(parameters.id);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        {editor.mode === "create" ? "Создание роли" : "Редактирование роли"}
      </Typography>

      {editor.load.kind === "loading" ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : null}

      {editor.load.kind === "error" ? (
        <Alert severity="error">Не удалось загрузить данные: {editor.load.message}</Alert>
      ) : null}

      {editor.load.kind === "ready" ? (
        <Box component="form" display="flex" flexDirection="column" gap={2} onSubmit={(event) => { event.preventDefault(); editor.submit(); }}>
          {!editor.editable ? (
            <Alert severity="info">Системная роль доступна только для просмотра и не может быть изменена.</Alert>
          ) : null}

          <Box display="flex" gap={2}>
            <TextField
              label="Название"
              value={editor.name}
              onChange={(event) => editor.setName(event.target.value)}
              error={editor.fieldErrors.name !== undefined}
              helperText={editor.fieldErrors.name}
              disabled={!editor.editable}
              fullWidth
            />
            <TextField
              label="Уровень"
              type="number"
              value={editor.level}
              onChange={(event) => editor.setLevel(Number(event.target.value))}
              error={editor.fieldErrors.level !== undefined}
              helperText={editor.fieldErrors.level}
              disabled={!editor.editable}
              sx={{ width: 160 }}
            />
          </Box>

          <Typography variant="subtitle1">Пермишены</Typography>
          <PermissionCheckboxGroup catalog={editor.load.catalog} selected={editor.selected} disabled={!editor.editable} onToggle={editor.togglePermission} />

          {editor.fieldErrors.general !== undefined ? <Alert severity="error">{editor.fieldErrors.general}</Alert> : null}

          <Box display="flex" alignItems="center" gap={1}>
            {editor.mode === "edit" ? (
              <IfPermission permission="roles.delete">
                <Button variant="contained" color="error" disabled={!editor.editable || deletion.kind === "loading"} onClick={() => setDeleteDialogOpen(true)}>
                  Удалить
                </Button>
              </IfPermission>
            ) : null}
            <Button type="submit" variant="contained" sx={{ ml: "auto" }} disabled={editor.submitting || !editor.editable}>
              Сохранить
            </Button>
            <Button onClick={() => {
      void navigate("/roles");
    }} disabled={editor.submitting}>
              {editor.editable ? "Отмена" : "Назад"}
            </Button>
          </Box>
        </Box>
      ) : null}

      <DeleteRoleDialog open={deleteDialogOpen} roleName={editor.name} onConfirm={confirmDelete} onCancel={() => setDeleteDialogOpen(false)} />

      <Snackbar open={deletion.kind === "error"} autoHideDuration={6000} onClose={resetDeletion}>
        <Alert severity="error" onClose={resetDeletion}>
          {deletion.kind === "error" ? deletion.message : ""}
        </Alert>
      </Snackbar>
    </Box>
  );
}
